import React, { useState, useEffect } from 'react';
import { PenTool, Plus, Trash2, Edit, ChevronDown, ChevronUp, Eye, Save } from 'lucide-react';
import { contentAPI } from '../services/api';
import { toast } from 'sonner';

export const ExpressionEcriteManager = () => {
  const [blocs, setBlocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBloc, setEditingBloc] = useState(null);
  const [expandedBloc, setExpandedBloc] = useState(null);
  
  // Form state for new bloc
  const [formData, setFormData] = useState({
    name: '',
    tache1: { titre: '', instruction: '', sujet: '', imageUrl: '', modeleCorrection: '', minMots: 60, maxMots: 120, duree: 20, dureeSecondes: 0 },
    tache2: { titre: '', instruction: '', sujet: '', imageUrl: '', modeleCorrection: '', minMots: 120, maxMots: 150, duree: 30, dureeSecondes: 0 },
    tache3: { titre: '', instruction: '', sujet: '', imageUrl: '', modeleCorrection: '', minMots: 180, maxMots: 200, duree: 40, dureeSecondes: 0 }
  });

  useEffect(() => {
    loadBlocs();
  }, []);

  const loadBlocs = async () => {
    try {
      const loadedBlocs = await contentAPI.getBlocs('expression_ecrite');
      setBlocs(Array.isArray(loadedBlocs) ? loadedBlocs : []);
    } catch (error) {
      console.error('Error loading blocs:', error);
      toast.error('Erreur lors du chargement des blocs');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      tache1: { titre: '', instruction: '', sujet: '', imageUrl: '', modeleCorrection: '', minMots: 60, maxMots: 120, duree: 20, dureeSecondes: 0 },
      tache2: { titre: '', instruction: '', sujet: '', imageUrl: '', modeleCorrection: '', minMots: 120, maxMots: 150, duree: 30, dureeSecondes: 0 },
      tache3: { titre: '', instruction: '', sujet: '', modeleCorrection: '', minMots: 180, maxMots: 200, duree: 40, dureeSecondes: 0 }
    });
  };

  const handleCreateBloc = async () => {
    try {
      const blocNumber = blocs.length + 1;
      const newBloc = {
        id: `ee_bloc_${Date.now()}`,
        name: formData.name || `Bloc ${blocNumber}`,
        number: blocNumber,
        tasks: [
          { ...formData.tache1, tache: 1 },
          { ...formData.tache2, tache: 2 },
          { ...formData.tache3, tache: 3 }
        ],
        created_at: new Date().toISOString()
      };
      
      const result = await contentAPI.createBloc('expression_ecrite', newBloc);
      if (result.success) {
        toast.success(`Bloc "${newBloc.name}" créé avec succès`);
        resetForm();
        setShowForm(false);
        loadBlocs();
      } else {
        toast.error(result.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating bloc:', error);
      toast.error('Erreur lors de la création du bloc');
    }
  };

  const handleUpdateBloc = async () => {
    try {
      const updates = {
        name: formData.name || editingBloc.name,
        tasks: [
          { ...formData.tache1, tache: 1 },
          { ...formData.tache2, tache: 2 },
          { ...formData.tache3, tache: 3 }
        ]
      };
      
      await contentAPI.updateBloc('expression_ecrite', editingBloc.id, updates);
      toast.success('Bloc mis à jour avec succès');
      setEditingBloc(null);
      resetForm();
      loadBlocs();
    } catch (error) {
      console.error('Error updating bloc:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteBloc = async (blocId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce bloc ?')) {
      try {
        await contentAPI.deleteBloc('expression_ecrite', blocId);
        toast.success('Bloc supprimé');
        loadBlocs();
      } catch (error) {
        console.error('Error deleting bloc:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const handleEditBloc = (bloc) => {
    setEditingBloc(bloc);
    const tasks = bloc.tasks || bloc.taches || [];
    const tache1 = tasks.find(t => t.tache === 1) || {};
    const tache2 = tasks.find(t => t.tache === 2) || {};
    const tache3 = tasks.find(t => t.tache === 3) || {};
    
    setFormData({
      name: bloc.name,
      tache1: { ...formData.tache1, ...tache1, dureeSecondes: tache1.dureeSecondes || 0 },
      tache2: { ...formData.tache2, ...tache2, dureeSecondes: tache2.dureeSecondes || 0 },
      tache3: { ...formData.tache3, ...tache3, dureeSecondes: tache3.dureeSecondes || 0 }
    });
    setShowForm(true);
  };

  const tacheDescriptions = {
    1: 'Tâche 1 : Rédiger un message court (60-120 mots) - Message à un ami, réponse à une annonce, etc.',
    2: 'Tâche 2 : Rédiger un article ou une lettre formelle (120-150 mots) - Article de blog, lettre de réclamation, etc.',
    3: 'Tâche 3 : Rédiger un texte argumentatif (180-200 mots) - Donner son opinion, argumenter un point de vue, etc.'
  };

  const tacheTypes = {
    1: 'Message court',
    2: 'Narration / Blog',
    3: 'Argumentation'
  };

  const renderTacheForm = (tacheNum, tacheKey) => (
    <div className="space-y-4 p-4 bg-secondary/20 rounded-lg">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-purple-600 dark:text-purple-400">Tâche {tacheNum} - {tacheTypes[tacheNum]}</h4>
        <span className="text-xs text-muted-foreground">{formData[tacheKey].minMots}-{formData[tacheKey].maxMots} mots • {formData[tacheKey].duree} min {formData[tacheKey].dureeSecondes || 0}s</span>
      </div>
      <p className="text-xs text-muted-foreground">{tacheDescriptions[tacheNum]}</p>
      
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium">Titre du sujet</label>
          <input
            type="text"
            value={formData[tacheKey].titre}
            onChange={(e) => setFormData({
              ...formData,
              [tacheKey]: { ...formData[tacheKey], titre: e.target.value }
            })}
            className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
            placeholder={`Ex: ${tacheTypes[tacheNum]}`}
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Consigne / Instruction *</label>
          <textarea
            value={formData[tacheKey].instruction}
            onChange={(e) => setFormData({
              ...formData,
              [tacheKey]: { ...formData[tacheKey], instruction: e.target.value }
            })}
            className="w-full px-3 py-2 rounded-lg border bg-background text-sm min-h-[60px] mt-1"
            placeholder="Ex: Il s'agit de rédiger un message dans le but d'inviter, décrire, raconter..."
            required
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Sujet / Contexte *</label>
          <textarea
            value={formData[tacheKey].sujet}
            onChange={(e) => setFormData({
              ...formData,
              [tacheKey]: { ...formData[tacheKey], sujet: e.target.value }
            })}
            className="w-full px-3 py-2 rounded-lg border bg-background text-sm min-h-[80px] mt-1"
            placeholder="Décrivez le contexte ou le scénario du sujet..."
            required
          />
        </div>

        {/* Modèle de correction */}
        <div>
          <label className="text-sm font-medium text-green-600 dark:text-green-400">
            Modèle de correction (exemple de réponse)
          </label>
          <textarea
            value={formData[tacheKey].modeleCorrection}
            onChange={(e) => setFormData({
              ...formData,
              [tacheKey]: { ...formData[tacheKey], modeleCorrection: e.target.value }
            })}
            className="w-full px-3 py-2 rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-sm min-h-[100px] mt-1"
            placeholder="Rédigez un exemple de réponse correcte que l'utilisateur pourra consulter après avoir tenté l'exercice..."
          />
          <p className="text-xs text-muted-foreground mt-1">
            Ce modèle sera masqué par défaut. L'utilisateur pourra le révéler en cliquant sur "Masquer/Afficher".
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium">Min mots</label>
            <input
              type="number"
              value={formData[tacheKey].minMots}
              onChange={(e) => setFormData({
                ...formData,
                [tacheKey]: { ...formData[tacheKey], minMots: parseInt(e.target.value) }
              })}
              className="w-full h-9 px-2 rounded-lg border bg-background text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium">Max mots</label>
            <input
              type="number"
              value={formData[tacheKey].maxMots}
              onChange={(e) => setFormData({
                ...formData,
                [tacheKey]: { ...formData[tacheKey], maxMots: parseInt(e.target.value) }
              })}
              className="w-full h-9 px-2 rounded-lg border bg-background text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium">Durée (min)</label>
            <input
              type="number"
              value={formData[tacheKey].duree}
              onChange={(e) => setFormData({
                ...formData,
                [tacheKey]: { ...formData[tacheKey], duree: parseInt(e.target.value) }
              })}
              className="w-full h-9 px-2 rounded-lg border bg-background text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium">Durée (sec)</label>
            <input
              type="number"
              value={formData[tacheKey].dureeSecondes || 0}
              onChange={(e) => setFormData({
                ...formData,
                [tacheKey]: { ...formData[tacheKey], dureeSecondes: parseInt(e.target.value) }
              })}
              className="w-full h-9 px-2 rounded-lg border bg-background text-sm mt-1"
              max="59"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-500/10 p-3 rounded-lg">
            <PenTool className="h-6 w-6 text-purple-500" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Expression Écrite</h2>
            <p className="text-sm text-muted-foreground">{blocs.length} bloc(s) • 3 tâches par bloc • 60 min par examen</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setEditingBloc(null); resetForm(); }}
          data-testid="create-ee-bloc-btn"
          className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Créer un nouveau bloc</span>
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-card p-6 rounded-xl border shadow-sm animate-in">
          <h3 className="text-xl font-semibold mb-4">
            {editingBloc ? `Modifier ${editingBloc.name}` : 'Nouveau Bloc - Expression Écrite'}
          </h3>
          
          <div className="space-y-6">
            {/* Bloc Name */}
            <div>
              <label className="text-sm font-medium">Nom du bloc</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border bg-background text-sm mt-1"
                placeholder="Ex: Combinaison 1 - Avril 2026"
              />
            </div>

            {/* Tâches */}
            <div className="space-y-4">
              {renderTacheForm(1, 'tache1')}
              {renderTacheForm(2, 'tache2')}
              {renderTacheForm(3, 'tache3')}
            </div>

            {/* Submit */}
            <div className="flex space-x-3 pt-4">
              <button
                onClick={editingBloc ? handleUpdateBloc : handleCreateBloc}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold"
              >
                <Save className="h-5 w-5" />
                <span>{editingBloc ? 'Mettre à jour' : 'Créer le bloc'}</span>
              </button>
              <button
                onClick={() => { setShowForm(false); setEditingBloc(null); resetForm(); }}
                className="px-6 py-3 rounded-lg border hover:bg-secondary transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Blocs List */}
      <div className="space-y-4">
        <h3 className="font-semibold">Blocs d'exercices</h3>
        
        {blocs.length === 0 ? (
          <div className="bg-card p-8 rounded-xl border text-center">
            <PenTool className="h-16 w-16 text-purple-500 mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">Aucun bloc créé</p>
            <p className="text-sm text-muted-foreground mt-2">Cliquez sur "Créer un nouveau bloc" pour commencer</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {blocs.map((bloc) => (
              <div
                key={bloc.id}
                className="bg-card rounded-xl border shadow-sm overflow-hidden"
              >
                {/* Bloc Header */}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <span className="text-purple-600 font-bold text-lg">{bloc.number}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold">{bloc.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        3 tâches • Créé le {new Date(bloc.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setExpandedBloc(expandedBloc === bloc.id ? null : bloc.id)}
                      className="p-2 rounded-lg hover:bg-secondary transition-colors"
                      title="Voir les détails"
                    >
                      {expandedBloc === bloc.id ? <ChevronUp className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <button
                      onClick={() => handleEditBloc(bloc)}
                      className="p-2 rounded-lg hover:bg-secondary transition-colors text-blue-500"
                      title="Modifier"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteBloc(bloc.id)}
                      className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-red-500"
                      title="Supprimer"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Tâches Preview */}
                {expandedBloc === bloc.id && (
                  <div className="border-t p-4 bg-secondary/10 space-y-3">
                    {bloc.taches?.map((tache, tIndex) => (
                      <div key={tIndex} className="bg-background p-3 rounded-lg border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-purple-600 dark:text-purple-400">
                            Tâche {tache.tache} - {tacheTypes[tache.tache]}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {tache.minMots}-{tache.maxMots} mots • {tache.duree} min {tache.dureeSecondes || 0}s
                          </span>
                        </div>
                        <p className="text-sm font-medium">{tache.titre || 'Sans titre'}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tache.instruction}</p>
                        {tache.modeleCorrection && (
                          <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                              ✓ Modèle de correction disponible
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
